/* ============================================================
   Token storage — Cognito JWTs kept in whatever secure store the platform
   provides (the device keychain/keystore via expo-secure-store on native —
   see app/src/platform). Stored under *separate* keys (not one JSON blob):
   iOS Keychain has historically rejected values over ~2KB, and an
   id+access+refresh bundle can exceed that. Each token comfortably fits alone.
   ============================================================ */
import type { KeyValueStorage } from '../adapters';

const KEYS = {
  access: 'auth.accessToken',
  id: 'auth.idToken',
  refresh: 'auth.refreshToken',
  expiresAt: 'auth.expiresAt',
  origin: 'auth.origin',
} as const;

/** How the session was created. The two paths mint tokens through different
   Cognito front doors, so they're refreshed through the matching one: `oauth`
   (social, via the hosted /oauth2 endpoints) refreshes with a refresh_token
   grant on /oauth2/token, `password` (native email/password, via the
   cognito-idp API) refreshes with InitiateAuth REFRESH_TOKEN_AUTH. */
export type SessionOrigin = 'oauth' | 'password';

export interface StoredTokens {
  accessToken: string;
  idToken: string;
  /** Cognito omits this on refresh; we keep the prior one in that case. */
  refreshToken: string | null;
  /** epoch ms when the access/id tokens expire */
  expiresAt: number;
  origin: SessionOrigin;
}

export interface TokenStore {
  save(t: StoredTokens): Promise<void>;
  load(): Promise<StoredTokens | null>;
  clear(): Promise<void>;
}

export function createTokenStore(storage: KeyValueStorage): TokenStore {
  return {
    async save(t) {
      await Promise.all([
        storage.setItem(KEYS.access, t.accessToken),
        storage.setItem(KEYS.id, t.idToken),
        // only overwrite the refresh token when we actually got a new one
        t.refreshToken ? storage.setItem(KEYS.refresh, t.refreshToken) : Promise.resolve(),
        storage.setItem(KEYS.expiresAt, String(t.expiresAt)),
        storage.setItem(KEYS.origin, t.origin),
      ]);
    },
    async load() {
      const [accessToken, idToken, refreshToken, expiresAtRaw, originRaw] = await Promise.all([
        storage.getItem(KEYS.access),
        storage.getItem(KEYS.id),
        storage.getItem(KEYS.refresh),
        storage.getItem(KEYS.expiresAt),
        storage.getItem(KEYS.origin),
      ]);
      if (!accessToken || !idToken) return null;
      return {
        accessToken,
        idToken,
        refreshToken: refreshToken ?? null,
        expiresAt: expiresAtRaw ? Number(expiresAtRaw) : 0,
        // sessions stored before origin was tracked came from the hosted UI
        origin: originRaw === 'password' ? 'password' : 'oauth',
      };
    },
    async clear() {
      await Promise.all(Object.values(KEYS).map((k) => storage.removeItem(k)));
    },
  };
}
